/**
 * Types, functions for performing cleanup on faces in order to, for example, produce cleaner exports.
 * @module
 */
import * as Sentry from "@sentry/browser";
import { FaceCategory } from "building-types/materials/faceCategories";
import { ensureDefined } from "common/utils/assertOfType";
import type { Face } from "geom/brep";
import {
  faceCentroid,
  faceOrNullCopy,
  faceNew,
  faceOutlineBandOnPlane,
  faceSimplifyLoops,
  mergeAdjacentCoplanarFaces,
  mergeNonAdjacentCoplanarFaces,
  polyLine2AABB3OnPlane,
  transformFaceLoopBasis,
} from "geom/brep";
import {
  gatherOrientedCoplanarFaceGroups,
  gatherOrientedCoplanarFaceGroupsFast,
  type FaceWithMaterialKey,
} from "geom/coplanarFaceGroups";
import { DIST_EPSILON, HALF_PI } from "geom/core";
import type { Line3Segment } from "geom/line3";
import type { Mesh3 } from "geom/mesh";
import { getFacesFromMesh } from "geom/mesh";
import type { PlaneSide } from "geom/plane";
import {
  planeByThreePoints,
  planeEval,
  planeIsOrientedCoplanar,
  planeNormal,
  planeProject,
  vec3PlaneSide,
} from "geom/plane";
import type { Plane } from "geom/planeTypes";
import { PolygonRoundingMode } from "geom/polyclip/polyClip";
import { polygonIntersectionMulti } from "geom/polygon";
import {
  polyLine2Centroid,
  polyLine2Copy,
  polyLine2EdgesCollinearToLine,
  polyLine2MergeCollinearEdges,
  polyLine2Simplify,
} from "geom/polyLine2";
import type { PolyLine2, PolyLine2MultiPolygon } from "geom/polyLine2Types";
import { tessFaces3, TriangulationStrategy } from "geom/tess";
import { clearArray } from "geom/utils";
import { vec2Dot, vec2Normalize } from "geom/vec2";
import { vec3Add, vec3AngleBetween, vec3Normalize, vec3Sub } from "geom/vec3";
import type { DebugCallback } from "./debug";
import { DebugCallbackType, getDebugId, incDebugId } from "./debug";

// Re-export types from coplanarFaceGroups that were previously exported from here
export type { FaceWithMaterialKey } from "geom/coplanarFaceGroups";

interface FaceMergeContext {
  inputFaces: Face[];
  coplanarDistTolerance?: number;
  coplanarUvAngleTolerance?: number;

  // Used by the export debugger
  debugCallback?: DebugCallback;
  materialName?: string;
}

function copyFaces(faces: Face[]): Face[] {
  const copies: Face[] = [];
  for (const face of faces) {
    const copy = faceOrNullCopy(face);
    if (copy) {
      copies.push(copy);
    }
  }
  return copies;
}

function mergeAdjacentFacesWithDebugger(
  faces: Face[],
  debugCallback: DebugCallback | undefined,
  type: DebugCallbackType,
  id: string,
): Face | null {
  let mergedFace: Face | null = null;
  try {
    mergedFace = mergeAdjacentCoplanarFaces(faces);
  } catch (error) {
    Sentry.captureException(error);
    console.warn("Failed to merge adjacent coplanar faces; preserving source faces", error);
  }

  if (debugCallback) {
    if (mergedFace) {
      debugCallback({ type, id, in: faces.map((face) => faceOrNullCopy(face)), out: [faceOrNullCopy(mergedFace)] });
    }

    incDebugId();
  }

  return mergedFace;
}

function mergeNonAdjacentFacesWithDebugger(
  faces: Face[],
  debugCallback: DebugCallback | undefined,
  type: DebugCallbackType,
  id: string,
): Face[] {
  let mergedFaces: Face[] = [];
  try {
    mergedFaces = mergeNonAdjacentCoplanarFaces(faces, PolygonRoundingMode.SNAP_ROUND);
  } catch (error) {
    Sentry.captureException(error);
    console.warn("Failed to merge non-adjacent coplanar faces; preserving source faces", error);
  }
  if (faces.length > 0 && mergedFaces.length === 0) {
    mergedFaces = copyFaces(faces);
  }

  if (debugCallback) {
    debugCallback({
      type,
      id,
      in: faces.map((face) => faceOrNullCopy(face)),
      out: mergedFaces.map((face) => faceOrNullCopy(face)),
    });
    incDebugId();
  }

  return mergedFaces;
}

export function faceSimplifyLoopsWithDebugger(
  face: Face,
  distThresholdSq: number | undefined,
  angleThreshold: number | undefined,
  debugCallback: DebugCallback | undefined,
  id: string,
) {
  // The simplification mutates the face in place, so we need to copy it beforehand
  let originalFace: Face | null = null;

  if (debugCallback) {
    originalFace = faceOrNullCopy(face);
  }

  const simplifiedFace = faceSimplifyLoops(face, distThresholdSq, angleThreshold);

  if (debugCallback) {
    debugCallback({
      type: DebugCallbackType.SIMPLIFY_FACES,
      id,
      in: ensureDefined(originalFace),
      out: faceOrNullCopy(simplifiedFace),
    });
    incDebugId();
  }

  return simplifiedFace;
}

export function mergeCoplanarMeshFaces({
  mesh,
  faces,
  coplanarDistTolerance = DIST_EPSILON,
  collinearityTolerance = 0.1,
  triangulationStrategy = TriangulationStrategy.EARCUT,
  polygonRoundingMode = PolygonRoundingMode.ROUND,
}: {
  mesh?: Mesh3;
  faces?: Face[];
  coplanarDistTolerance?: number;
  collinearityTolerance?: number;
  triangulationStrategy?: TriangulationStrategy;
  polygonRoundingMode?: PolygonRoundingMode;
}): Mesh3 {
  if (mesh) {
    faces = getFacesFromMesh(mesh);
  }

  const coplanarFaceGroups = gatherOrientedCoplanarFaceGroupsFast(faces, coplanarDistTolerance);
  const mergedFaces: Face[] = [];

  for (const coplanarFaceGroup of coplanarFaceGroups) {
    const coplanarFaces = mergeNonAdjacentCoplanarFaces(coplanarFaceGroup, polygonRoundingMode).map((face) => {
      const simplifiedLoop = polyLine2MergeCollinearEdges(face.loops[0], collinearityTolerance);
      polyLine2Simplify(simplifiedLoop);
      return faceNew([simplifiedLoop], face.plane);
    });
    mergedFaces.push(...coplanarFaces);
  }

  return tessFaces3(mergedFaces, undefined, undefined, triangulationStrategy);
}

export function mergeFacesOfSameCategory(
  faceCategory: FaceCategory | undefined,
  inputFaces: Face[],
  coplanarDistTolerance: number | undefined,
  debugCallback?: DebugCallback,
  adjacentFaces: boolean = true,
): Face[] {
  if (!coplanarDistTolerance) {
    coplanarDistTolerance = DIST_EPSILON;
  }

  const mergedFaces: Face[] = [];
  const mergeFaces = (faces: Face[]): Face[] => {
    if (adjacentFaces) {
      const face = mergeAdjacentFacesWithDebugger(
        faces,
        debugCallback,
        DebugCallbackType.MERGE_FACES_OF_SAME_CATEGORY,
        `${getDebugId()}-${faceCategory !== undefined ? FaceCategory[faceCategory] : "undefined"}`, // matches current behavior
      );
      if (face) {
        return [face];
      }
      return copyFaces(faces);
    } else {
      return mergeNonAdjacentFacesWithDebugger(
        faces,
        debugCallback,
        DebugCallbackType.MERGE_FACES_OF_SAME_CATEGORY,
        `${getDebugId()}-${faceCategory !== undefined ? FaceCategory[faceCategory] : "undefined"}`, // matches current behavior
      );
    }
  };

  // Faces are emitted in coplanar batches, so the algorithm is straightforward:
  // - we group faces as long as they are coplanar
  // - we merge them when we find one that's not coplanar
  // - repeat on the last face

  const alignedFaces: Face[] = [];
  let currentPlane: Plane | undefined = undefined;

  for (const inputFace of inputFaces) {
    if (!currentPlane) {
      currentPlane = inputFace.plane;
    } else if (!planeIsOrientedCoplanar(inputFace.plane, currentPlane, coplanarDistTolerance)) {
      mergedFaces.push(...mergeFaces(alignedFaces));
      clearArray(alignedFaces);

      currentPlane = inputFace.plane;
    }

    alignedFaces.push(inputFace);
  }

  // Merge remaining aligned faces
  if (alignedFaces.length > 0) {
    mergedFaces.push(...mergeFaces(alignedFaces));
  }

  return mergedFaces;
}

export function mergeFacesOfSameMaterial(args: FaceMergeContext): Face[] {
  const { inputFaces, coplanarDistTolerance, coplanarUvAngleTolerance, debugCallback, materialName } = args;

  const mergedFaces: Face[] = [];
  const mergeFaces = (faces: Face[]): Face[] =>
    mergeNonAdjacentFacesWithDebugger(
      faces,
      debugCallback,
      DebugCallbackType.MERGE_FACES_OF_SAME_MATERIAL,
      `${getDebugId()}-${materialName}`,
    );

  const coplanarFaceGroups = gatherOrientedCoplanarFaceGroups({
    inputFaces,
    coplanarFaceGroups: [],
    coplanarDistTolerance,
    coplanarUvAngleTolerance,
  });

  for (const coplanarFaceGroup of coplanarFaceGroups) {
    mergedFaces.push(...mergeFaces(coplanarFaceGroup));
  }

  return mergedFaces;
}

export function detectMaterialOverlap(coplanarFaceGroups: FaceWithMaterialKey[][], debugCallback: DebugCallback) {
  for (const coplanarFaceGroup of coplanarFaceGroups) {
    const groupPlane = coplanarFaceGroup[0].plane;
    const keySet = new Set(coplanarFaceGroup.map((face) => face.materialKey));
    if (keySet.size > 1) {
      const groupByMaterial: { [key: string]: PolyLine2MultiPolygon } = {};
      for (const face of coplanarFaceGroup) {
        if (!groupByMaterial[face.materialKey]) {
          groupByMaterial[face.materialKey] = [];
        }

        const pl = polyLine2Copy(face.loops[0]);
        transformFaceLoopBasis(pl, face.plane, groupPlane);
        groupByMaterial[face.materialKey].push([pl]);
      }

      const intersection = polygonIntersectionMulti(Object.values(groupByMaterial));
      if (intersection.length > 0) {
        debugCallback({
          type: DebugCallbackType.DETECT_MATERIAL_OVERLAP,
          id: getDebugId().toString(),
          in: coplanarFaceGroup.map((face) => faceOrNullCopy(face)),
          out: intersection.map((polygon) => faceNew(polygon, groupPlane)),
        });
        incDebugId();
      }
    }
  }
}

export function computeMiterPlane(faceA: Face, faceB: Face, line: Line3Segment): Plane | null {
  const [x0, y0, z0, x1, y1, z1] = line;

  // We compute the inner face position (relative to [x0,y0,z0]) by:
  // - projecting the line into the plane and extracting its direction
  // - rotating that direction 90 degrees
  // - the heuristic we use to select the corresponding normal is that it should be in the same half-plane
  //   as the vector to the face centroid. In practice, the miter line is one the furthest distances from the centroid.
  //   To handle degenerate cases, we intersect the silhouette with the band corresponding to the miter line and
  //   use that result to compute the centroid.
  // - we then apply that normal from the starting point to determine the location
  const innerFacePositionOrthogonalToLine = (plane, outerLoopInBand) => {
    const [startU, startV] = planeProject(plane, x0, y0, z0);
    const [endU, endV] = planeProject(plane, x1, y1, z1);
    const [dirU, dirV] = vec2Normalize(endU - startU, endV - startV);

    const centroid = polyLine2Centroid(outerLoopInBand[0]);
    if (!centroid) {
      return null;
    }
    const [centroidU, centroidV] = centroid;
    const [centroidDirU, centroidDirV] = vec2Normalize(centroidU - startU, centroidV - startV);

    let [normalU, normalV] = [-dirV, dirU];
    if (vec2Dot(normalU, normalV, centroidDirU, centroidDirV) < 0) {
      normalU *= -1;
      normalV *= -1;
    }

    return planeEval(plane, startU + normalU, startV + normalV);
  };

  // We limit the centroid computation on outerLoop intersected with height band (only works for vertical miters)
  //
  // Bail early if that outer loop doesn't exist
  const outerLoopInBandA = faceOutlineBandOnPlane(faceA, z0, z1);
  const outerLoopInBandB = faceOutlineBandOnPlane(faceB, z0, z1);
  if (outerLoopInBandA.length === 0 || outerLoopInBandB.length === 0) {
    return null;
  }

  // Once we have both inner face positions, we can determine the miter plane using their midpoint:
  const a = innerFacePositionOrthogonalToLine(faceA.plane, outerLoopInBandA);
  if (!a) {
    return null;
  }
  const [aX, aY, aZ] = a;
  const b = innerFacePositionOrthogonalToLine(faceB.plane, outerLoopInBandB);
  if (!b) {
    return null;
  }
  const [bX, bY, bZ] = b;
  return planeByThreePoints(
    0.5 * (x0 + x1),
    0.5 * (y0 + y1),
    0.5 * (z0 + z1),
    x1,
    y1,
    z1,
    0.5 * (aX + bX),
    0.5 * (aY + bY),
    0.5 * (aZ + bZ),
  );
}

export function miterPlaneSide(face: Face, miterPlane: Plane, miterSegment: Line3Segment): PlaneSide | null {
  const [x0, y0, z0, x1, y1, z1] = miterSegment;

  const outerLoopsInBand = faceOutlineBandOnPlane(face, z0, z1);
  let outerLoopInBand: PolyLine2;
  if (outerLoopsInBand.length > 1) {
    const [u0, v0] = planeProject(face.plane, x0, y0, z0);
    const [u1, v1] = planeProject(face.plane, x1, y1, z1);

    outerLoopInBand = outerLoopsInBand.filter(
      (loop) => polyLine2EdgesCollinearToLine(loop, u0, v0, u1, v1).length > 0,
    )[0];
  } else {
    outerLoopInBand = outerLoopsInBand[0];
  }

  // Bail early if we weren't able to determine the outer loop in that band
  if (!outerLoopInBand) {
    return null;
  }

  const outerLoopInBandAabb3 = polyLine2AABB3OnPlane(outerLoopInBand, face.plane);

  const [centerX, centerY, centerZ] = [
    0.5 * (outerLoopInBandAabb3.minX + outerLoopInBandAabb3.maxX),
    0.5 * (outerLoopInBandAabb3.minY + outerLoopInBandAabb3.maxY),
    0.5 * (outerLoopInBandAabb3.minZ + outerLoopInBandAabb3.maxZ),
  ];

  const [normalX, normalY, normalZ] = planeNormal(miterPlane);
  return vec3PlaneSide(centerX, centerY, centerZ, x0, y0, z0, normalX, normalY, normalZ);
}

export function isMiterOnInsideCorner(faceA: Face, faceB: Face, miterSegment: Line3Segment) {
  const [x0, y0, z0, , , ,] = miterSegment;

  // Determine vectors from the miter segment to the face centroids
  const [acX, acY, acZ] = ensureDefined(faceCentroid(faceA));
  const [adX, adY, adZ] = vec3Sub(acX, acY, acZ, x0, y0, z0);

  const [bcX, bcY, bcZ] = ensureDefined(faceCentroid(faceB));
  const [bdX, bdY, bdZ] = vec3Sub(bcX, bcY, bcZ, x0, y0, z0);

  // Add them together to create the discriminant vector:
  // - for inside corners: it will be pointing towards the outside
  // - for outside corners: it will be pointing towards the inside
  const [dX, dY, dZ] = vec3Add(adX, adY, adZ, bdX, bdY, bdZ);
  const [dnX, dnY, dnZ] = vec3Normalize(dX, dY, dZ);

  // Compare with face normals
  const [anX, anY, anZ] = planeNormal(faceA.plane);
  const [bnX, bnY, bnZ] = planeNormal(faceB.plane);

  const aAngle = Math.abs(vec3AngleBetween(anX, anY, anZ, dnX, dnY, dnZ));
  const bAngle = Math.abs(vec3AngleBetween(bnX, bnY, bnZ, dnX, dnY, dnZ));

  return aAngle < HALF_PI && bAngle < HALF_PI;
}
